import React, { useState, useEffect } from 'react';
import {
  TextFieldProps,
  TextField,
} from '@lifeomic/chroma-react/components/TextField';
import { defineMessages, useIntl } from 'react-intl';
import moment, { Moment } from 'moment';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import { getTestProps } from '../../../../../util/testProps';

export const testIds = {
  dateTimeFieldInput: 'dateTimeFieldInput',
};

const messages = defineMessages({
  example: {
    id: 'DateTimeField.exampleHelpMessage',
    defaultMessage: 'Example: "{example}"',
  },
  invalidFormatError: {
    id: 'DateTimeField.invalidFormatError',
    defaultMessage: 'Input must be in the format: "{format}"',
  },
  date: {
    id: 'DateTimeField.date',
    defaultMessage: 'Date',
  },
});

export type InputType = 'date' | 'time' | 'dateTime';

export const momentFormats = {
  date: 'YYYY-MM-DD',
  time: 'HH:mm',
  dateTime: 'YYYY-MM-DDTHH:mm',
};

const invalidMoment = moment.invalid({ empty: true });

const momentToString = (momentVal: Moment, inputType: InputType): string => {
  if (!momentVal || !momentVal?.isValid()) return '';

  const momentFormat = momentFormats[inputType];
  return momentVal.format(momentFormat);
};

const isEmptyInput = (value: string) =>
  isNil(value) || !isString(value) || !value;

const stringToMoment = (value: string, inputType: InputType): Moment => {
  if (isEmptyInput(value)) return invalidMoment;

  let inputString = value;
  let inputFormat = momentFormats[inputType];
  if (inputType === 'time') {
    // time types must be padded with a date.  Moment does not handle pure time values
    inputString = `2000-01-01T${value}`;
    inputFormat = momentFormats.dateTime;
  }

  const asMoment = moment(inputString, inputFormat);

  const isValid =
    !!asMoment &&
    !!asMoment?.isValid() &&
    asMoment.format(inputFormat) === inputString;

  return isValid ? asMoment : invalidMoment;
};

export interface DateFieldOwnProps {
  disableMinutes?: boolean;
  input?: Omit<TextFieldProps, 'value' | 'onChange' | 'onFocus' | 'onBlur'>;
  inputType: InputType;
  value: Moment | null;
  onChange: (moment: Moment | null) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

export type DateTimeFieldProps = Omit<TextFieldProps, 'onChange' | 'value'> &
  DateFieldOwnProps;

/**
 * This field abstracts away the complexities of date inputs.  It will render a masked input and
 * handle all formatting/validation.  The API is as simple as a moment-value controlled component.
 * If the date value is invalid, the parent component just recieve "null" as the value
 */
const DateTimeField = ({
  disableMinutes, // use if you only want to enable hours
  inputType,
  value,
  onChange,
  input = {},
  onFocus,
  onBlur,
  ...rest
}: DateTimeFieldProps) => {
  const { formatMessage } = useIntl();
  const [workingValue, setWorkingValue] = useState<string>(() =>
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    momentToString(value, inputType),
  );

  // any parent updates should update working value
  useEffect(() => {
    if (!value || !value?.isValid()) setWorkingValue('');

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    const stringRepresentation = momentToString(value, inputType);
    setWorkingValue(stringRepresentation);
  }, [setWorkingValue, value, inputType]);

  // intercept and keep track of touched internally for own formatting validation
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur && onBlur(e);
  };

  // intercept change and keep track of internal working string value.  Only update parent with valid dates
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e?.target?.value || '';

    setWorkingValue(value);

    // only update parent with value if valid format
    const asMomentValue = stringToMoment(value, inputType);
    const updateValue = asMomentValue.isValid() ? asMomentValue : null;
    onChange(updateValue);
  };

  return (
    <TextField
      {...getTestProps(testIds.dateTimeFieldInput)}
      {...rest}
      {...input}
      color="default"
      onChange={handleChange}
      value={workingValue}
      onBlur={handleBlur}
      onFocus={onFocus}
      aria-label={formatMessage(messages.date)}
      type={
        {
          date: 'date',
          time: 'time',
          dateTime: 'datetime-local',
        }[inputType]
      }
    />
  );
};

export default React.memo(DateTimeField);
